/**
 * @file MemoryMacroStore.ts
 * @description 复用内存地址型宏的读取、写入、分包和激活流程。
 */

import type { BaseDevice } from '../../../../runtime/BaseDevice'
import type { Macro, PressureMacro, Action } from '../../../../types/macro'
import type { CommonAddress } from '../../../../types/protocol'
import { convertASCIIToStr, convertStrToASCII } from '../../../../utils/StringUtils'
import { delay } from '../../../../utils/TimeUtils'
import { getAddress } from '../../../../utils/common'
import {
  DEFAULT_MACRO_MAX_LENGTH,
  encodeMacroData,
  getMacroMaxLength,
  parseMacroActions,
} from '../../../../utils/macro'
import type { MemoryMacroProfile, MemoryMacroRead, MemoryMacroWrite } from './MemoryMacroProfile'

type MemoryMacro = Macro | PressureMacro

export interface MemoryMacroStoreOptions {
  device: BaseDevice
  profile: MemoryMacroProfile
  read: MemoryMacroRead
  write: MemoryMacroWrite
}

/**
 * 这里只处理“宏如何存到设备内存里”，不处理键盘/鼠标 Feature 的挂载和业务含义。
 */
export class MemoryMacroStore<TMacro extends MemoryMacro> {
  private readonly device: BaseDevice
  private readonly profile: MemoryMacroProfile
  private readonly read: MemoryMacroRead
  private readonly write: MemoryMacroWrite

  constructor(options: MemoryMacroStoreOptions) {
    this.device = options.device
    this.profile = options.profile
    this.read = options.read
    this.write = options.write
  }

  async getMacroList(): Promise<TMacro[]> {
    const addresses = this.requireNameAddresses()
    const list = await Promise.all(
      addresses.map((address, index) => this.readMacroName(address, index)),
    )
    return list.filter((macro) => macro.name) as TMacro[]
  }

  async *getMacroListStream(
    batchSize: number = this.profile.listBatchSize,
  ): AsyncIterable<TMacro[]> {
    const addresses = this.requireNameAddresses()

    for (let start = 0; start < addresses.length; start += batchSize) {
      const end = Math.min(start + batchSize, addresses.length)
      const batchAddresses = addresses.slice(start, end)
      const batch = await Promise.all(
        batchAddresses.map((address, batchIndex) =>
          this.readMacroName(address, start + batchIndex),
        ),
      )
      const validMacros = batch.filter((macro) => macro.name) as TMacro[]

      if (validMacros.length > 0) {
        yield validMacros
      }

      await new Promise((resolve) => setTimeout(resolve, 0))
    }
  }

  async getMacroByIndex(index: number): Promise<TMacro> {
    const { loop, ignoreDelay, actions } = await this.readMacroPayload(index)
    const macro = {
      loop,
      index,
      ignoreDelay,
      package: actions,
    } as TMacro

    if (this.profile.nameAddresses) {
      ;(macro as Macro).name = await this.readMacroDetailName(index)
    }

    return macro
  }

  async setMacroByIndex(macro: TMacro, index: number): Promise<void> {
    await this.writeMacroPayload(macro, index)

    if (this.profile.nameAddresses) {
      await this.writeMacroName(macro as Macro, index)
    }

    if (this.profile.activationDelayMs) {
      await delay(this.profile.activationDelayMs)
    }
  }

  private async readMacroName(address: CommonAddress, index: number): Promise<Macro> {
    return this.read(this.device, address, 0x10, (res: DataView) => {
      const bytes = new Uint8Array(res.buffer, res.byteOffset + 4, 0x10)
      return {
        name: this.profile.isEmptyListName(bytes) ? '' : this.decodeName(bytes),
        index,
      }
    })
  }

  private async readMacroDetailName(index: number): Promise<string> {
    const address = getAddress(this.requireNameAddresses(), index)
    return this.read(this.device, address, 0x10, (res: DataView) => {
      const bytes = new Uint8Array(res.buffer, res.byteOffset + 4, 0x10)
      const isEmpty = this.profile.isEmptyDetailName ?? this.profile.isEmptyListName
      return isEmpty(bytes) ? '' : this.decodeName(bytes)
    })
  }

  private async readMacroPayload(
    index: number,
  ): Promise<{ loop: number; ignoreDelay: boolean; actions: Action[] }> {
    const address = getAddress(this.profile.packageAddresses, index)
    let macroLen = 0
    let loop = 0

    await this.read(this.device, address, 0x08, (res: DataView) => {
      const validOffset = 4
      macroLen = res.getUint16(validOffset, true)
      loop = res.getUint16(validOffset + 2, true)
    })

    // 空槽 / 恢复出厂后 flash 擦成 0xFF 时，长度会读成 0 或 0xFFFF(65535)
    const maxMacroPayloadLen = this.getEncodeMaxLen()
    if (this.profile.allowEmptyMacroPayload && (macroLen <= 0 || macroLen > maxMacroPayloadLen)) {
      return {
        loop: 0,
        ignoreDelay: false,
        actions: [],
      }
    }

    const fullMacroBytes: number[] = []
    const readLength = this.profile.packageReadLength ?? this.profile.maxPackageLen
    const readPromises = this.profile
      .generatePackageAddresses(address, macroLen, this.profile.maxPackageLen)
      .map((realAddress) =>
        this.read(this.device, realAddress, readLength, (res: DataView) => {
          const validOffset = 4
          const validData = new DataView(
            res.buffer,
            res.byteOffset + validOffset,
            res.byteLength - validOffset,
          )
          const alreadyRead = fullMacroBytes.length
          const remainNeedRead = macroLen - alreadyRead
          const currentPkgReadLen = Math.min(
            validData.byteLength,
            this.profile.maxPackageLen,
            remainNeedRead,
          )

          for (let i = 0; i < currentPkgReadLen; i++) {
            fullMacroBytes.push(validData.getUint8(i))
          }
        }),
      )

    await Promise.all(readPromises)

    const { actions, ignoreDelay } = parseMacroActions(fullMacroBytes)
    return {
      loop,
      ignoreDelay,
      actions,
    }
  }

  private async writeMacroPayload(macro: TMacro, index: number): Promise<void> {
    const maxLen = this.getEncodeMaxLen()
    const { fullMacroBytes, macroLength } = encodeMacroData(macro, maxLen)
    const address = getAddress(this.profile.packageAddresses, index)
    let sentBytes = 0

    const writes = this.profile
      .generatePackageAddresses(address, macroLength, this.profile.maxPackageLen)
      .map((writeAddress) => {
        const currentPkgData = fullMacroBytes.slice(
          sentBytes,
          sentBytes + this.profile.maxPackageLen,
        )
        sentBytes += currentPkgData.length
        return this.write(this.device, writeAddress, Array.from(currentPkgData))
      })

    await Promise.all(writes)
    await this.activate(address)
  }

  private async writeMacroName(macro: Macro, index: number): Promise<void> {
    const address = getAddress(this.requireNameAddresses(), index)
    const rawNameBytes = convertStrToASCII(macro.name ?? `Macro${index + 1}`)
    const nameBytes = [...rawNameBytes.slice(0, 16), ...Array(16).fill(0)].slice(0, 16)

    await this.write(this.device, address, nameBytes)
    await this.activate(address)
  }

  private async activate(address: CommonAddress): Promise<void> {
    const activationAddress = [...address]
    activationAddress[0] += 1
    await this.write(this.device, activationAddress, [])
  }

  private decodeName(bytes: Uint8Array): string {
    return convertASCIIToStr(Array.from(bytes, (value) => value.toString(16)))
  }

  private requireNameAddresses(): CommonAddress[] {
    if (!this.profile.nameAddresses) {
      throw new Error('Memory macro profile does not define name addresses.')
    }
    return this.profile.nameAddresses
  }

  /** 优先使用 manifest `macro.maxLength`，否则回退到 profile / 键盘默认 512。 */
  private getEncodeMaxLen(): number {
    return getMacroMaxLength(
      this.device.manifest.capabilities,
      this.profile.maxMacroPayloadLen ?? DEFAULT_MACRO_MAX_LENGTH,
    )
  }
}
